import { useEffect, useState } from "react";

// DEPENDENCIES
import * as Yup from "yup";
import * as Sentry from "@sentry/react";
import { saveAs } from "file-saver";
import { useFormik } from "formik";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { toTestId } from "../../app/utils";
import { useNavigate, useSearchParams } from "react-router-dom";

// UTILS
import {
  genericToastifyError,
  getCompanyCuit,
  getPersonType,
  hasJointSignature,
  isAllariaFondosOnboarding,
  isCoowner,
} from "../../utils";
import {
  onUpdateCompletedForm,
  onUpdateStep,
} from "../../reducers/onboardingDataReducer";
import {
  onUpdateCompletedForm as onUpdateCompletedFormSecurities,
  onUpdateStep as onUpdateStepSecurities,
} from "../../reducers/securitiesReducer";
import { onUpdatePreconfirmation } from "../../reducers/preconfirmationReducer";
import useWindowWidth from "../../hooks/useWindowWidth";
import { logAnalyticsAndHotjarEvent } from "../../main";
import useTemplateInterpreter from "../../hooks/useTemplateInterpreter";
import { getPartnership } from "../../utils/companies";

// TYPES
import { Preconfirmation } from "../../types";
import { OnboardingType } from "../../types";

// CONNECTORS
import {
  completeDraft,
  draftExists,
  isBlockedOnboardingError,
} from "../../connectors/connectors";

// COMPONENTS
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import { Checkbox } from "@allaria-tech/matter/Checkbox";
import { ButtonGroup } from "@allaria-tech/matter/ButtonGroup";
import PreconfirmationCard from "../PreconfirmationCard/PreconfirmationCard";
import Layout from "../Layout/Layout";

// STYLES
import styles from "./PreconfirmationPage.module.scss";

const PreconfirmationPage = () => {
  const {
    container,
    checkboxesGroup,
    checkboxRow,
    checkboxLabel,
    optionalText,
    optionalBox,
    mobileActionsBar,
  } = styles;

  const [isDisabled, setIsDisabled] = useState(true);
  const [allariaAccountExists, setAllariaAccountExists] = useState<
    boolean | null
  >(null);

  const [searchParams] = useSearchParams();
  const templateURL = searchParams.get("template");

  const state = useAppSelector((state) => state);
  const { steps } = state.activeTemplate;

  // Fall back to template param, in case something went wrong and state reset.
  // We can't do default value on destructure it's `null` when unset, not `undefined`.
  let { onboardingType } = state.activeTemplate;
  onboardingType = onboardingType ?? (templateURL as OnboardingType) ?? null;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [preconfirmationData, setPreconfirmationData] =
    useState<Preconfirmation | null>(null);

  const fieldsInitialValues =
    preconfirmationData?.checkboxes?.reduce(
      (acc: Record<string, boolean>, { name, initialValue }) => {
        acc[name] = initialValue;
        return acc;
      },
      {},
    ) || {};

  const fieldsValidations = preconfirmationData?.checkboxes?.reduce(
    (acc: Record<string, Yup.Schema>, { name, validation }) => {
      acc[name] = validation;
      return acc;
    },
    {},
  );

  const formik = useFormik({
    initialValues: fieldsInitialValues,
    validationSchema: Yup.object().shape({ ...fieldsValidations }),
    onSubmit: async (values) => {
      try {
        if (preconfirmationData?.onSubmit)
          return await preconfirmationData.onSubmit({
            values,
            state,
            dispatch,
            navigate,
          });

        if (preconfirmationData?.sendTermsAndConditions)
          await preconfirmationData.sendTermsAndConditions(state, values);

        await completeDraft({ draftUUID });

        dispatch(
          onboardingType === "SECURITIES"
            ? onUpdateCompletedFormSecurities(true)
            : onUpdateCompletedForm(true),
        );
        dispatch({ type: "reset-store" });
        resetForm();
        logAnalyticsAndHotjarEvent("onboarding_completed", {
          personType: onboardingType,
        });
        navigate(successPage);
      } catch (error) {
        if (isBlockedOnboardingError(error)) {
          navigate(urlTemplate);
          return;
        }
        console.log(error);
        Sentry.captureException(error);
        showToastMessage(genericToastifyError, {
          containerId: "layoutPage",
          type: "ERROR",
        });
      }
    },
  });

  const { values, handleSubmit, isSubmitting, setFieldValue, resetForm } =
    formik;

  const { loadTemplate, urlTemplate, draftUUID, formValues } =
    useTemplateInterpreter({
      dispatch,
      onboardingType,
      state,
    });

  const previousStep = steps && steps?.length - 1;

  const handleBack = () => {
    dispatch(onUpdatePreconfirmation(true));
    navigate(urlTemplate);
    dispatch(
      onboardingType === "SECURITIES"
        ? onUpdateStepSecurities(Number(previousStep))
        : onUpdateStep(Number(previousStep)),
    );
  };

  const linksData = preconfirmationData?.checkboxes?.flatMap(
    ({ links }) => links || [],
  );
  const linksElements = linksData?.map(({ id }) => document.getElementById(id));

  const handleLabelClick = (e: React.MouseEvent<Element>) => {
    const target = e.target as Node;

    // Find if user clicked on a link to terms or notes
    const linkClicked = linksElements?.find((link) => link?.contains(target));

    if (!linkClicked) return;

    // Downloading the document shouldn't toggle the checkbox the label belongs to
    e.preventDefault();
    e.stopPropagation();

    const linkData = linksData?.find(({ id }) => id === linkClicked.id);
    saveAs(linkData?.url || "", `${linkData?.name}.pdf`);
  };

  const dimensions = useWindowWidth();
  const isMobile = dimensions.width < 768;

  const partnership = getPartnership(formValues);
  const partnershipKey =
    partnership?.key && partnership?.key !== "labragadense"
      ? partnership.key
      : null;

  const successSearchParams = new URLSearchParams({
    [partnershipKey ?? ""]: partnership ? "true" : "",
    allariaSecurities: onboardingType === "SECURITIES" ? "true" : "",
    signatories: hasJointSignature(formValues) ? "true" : "",
    coowners: isCoowner() ? "true" : "",
    language: state.securities.language ?? "es",
  });

  const successPage = `/registro-enviado${
    successSearchParams.toString() ? `?${successSearchParams.toString()}` : ""
  }`;

  useEffect(() => {
    if (!state.preconfirmation.isFromNormalFlow) {
      loadTemplate();
    }
  }, [onboardingType]);

  useEffect(() => {
    const { preconfirmation } = state.activeTemplate;

    if (preconfirmation && formValues) {
      const preconfirmationData = preconfirmation({
        values: formValues || {},
      });
      setPreconfirmationData(preconfirmationData);
    }
  }, [state.activeTemplate, formValues]);

  useEffect(() => {
    if (values.termsAndConditions) {
      logAnalyticsAndHotjarEvent("terms_accepted");
    }
  }, [values]);

  //The CUIT and the personType are one pair, not two choices: the signatory
  //flow carries both the company's CUIT and the signatory's own CUIL, so
  //picking them separately asks about one holder while labelling it the other
  const personType = onboardingType ? getPersonType(onboardingType) : null;
  const holderCuit =
    personType === "JURIDICAL" ? getCompanyCuit(formValues) : formValues?.cuit;

  useEffect(() => {
    if (!isAllariaFondosOnboarding() || !personType || !holderCuit) return;

    draftExists({ cuit: holderCuit, personType })
      .then((res) => setAllariaAccountExists(res.data))
      .catch(() => setAllariaAccountExists(false));
  }, [holderCuit, personType]);

  useEffect(() => {
    // When the user reaches the "preconfirmation" without having gone through the flow, we redirect them
    if (!draftUUID) {
      const obTypeToNavigate = urlTemplate;
      if (!obTypeToNavigate.includes("firmante")) {
        navigate(urlTemplate);
      } else navigate(`/empresarial`);
    }
  }, [preconfirmationData]);

  useEffect(() => {
    if (preconfirmationData) {
      if (
        !preconfirmationData?.checkboxes?.length ||
        !preconfirmationData?.sendTermsAndConditions
      ) {
        setIsDisabled(false);
      } else {
        const requiredFieldsAreEmpty = preconfirmationData.checkboxes.some(
          ({ name, required }) => required && !values[name],
        );
        setIsDisabled(requiredFieldsAreEmpty);
      }
    }
  }, [preconfirmationData, values]);

  return (
    <Layout hideStepper dataId="preconfirmation" className={container}>
      <PreconfirmationCard
        preconfirmationSteps={preconfirmationData?.steps || []}
      />

      <ButtonGroup
        container={isMobile}
        containerClassName={isMobile ? mobileActionsBar : undefined}
        secondary={{ text: "Volver", type: "button", onClick: handleBack }}
        primary={{
          text: preconfirmationData?.submitButtonText
            ? preconfirmationData.submitButtonText({ state, formValues })
            : "Continuar",
          type: "submit",
          onClick: () => handleSubmit(),
          isLoading: isSubmitting,
          isDisabled: isDisabled || isSubmitting,
        }}
      >
        <form className={checkboxesGroup}>
          {preconfirmationData?.checkboxes?.map(({ name, label, required }) => {
            const resolvedLabel =
              name === "wantsAllariaAccount" && allariaAccountExists === true ? (
                <>
                  Acepto el{" "}
                  <span id="allaria-platform-terms">
                    Uso de plataforma de Allaria
                  </span>
                </>
              ) : (
                label
              );
            return (
              <div className={checkboxRow} key={name}>
                <Checkbox
                  name={name}
                  testId={toTestId(typeof label === "string" ? label : name)}
                  checked={!!values[name]}
                  onCheckedChange={(checked) =>
                    setFieldValue(name, checked === true)
                  }
                  label={
                    <span
                      className={checkboxLabel}
                      id={`label-${name}`}
                      onClick={handleLabelClick}
                    >
                      {resolvedLabel}
                      {!required && isMobile && (
                        <span className={optionalText}> (opcional)</span>
                      )}
                    </span>
                  }
                />
                {!required && !isMobile && (
                  <span className={optionalBox}>Opcional</span>
                )}
              </div>
            );
          })}
        </form>
      </ButtonGroup>
    </Layout>
  );
};

export default PreconfirmationPage;
